import { useCallback, useEffect, useState } from 'react';
import { MAX_HOME_ITEMS } from '../homeUtils';
import type { HomeUser, PullRequest } from '../homeTypes';

const PULL_REQUESTS_REFRESH_INTERVAL = 5400000; // 1.5 hours (90 minutes)

export function useHomePullRequests(
  backendUrl: string,
  githubUsername: string | undefined,
  refreshIdentity: () => Promise<HomeUser>,
): {
  pullRequests: PullRequest[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [pullRequests, setPullRequests] = useState<PullRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPullRequests = useCallback(
    async (username?: string) => {
      if (!username) {
        setPullRequests([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          state: 'open',
          limit: String(MAX_HOME_ITEMS),
          searchBy: 'username',
        });
        const response = await fetch(
          `${backendUrl}/api/github/users/${encodeURIComponent(
            username,
          )}/pulls?${params}`,
        );

        if (!response.ok)
          throw new Error(`GitHub request failed: ${response.status}`);
        const data: { items?: PullRequest[] } = await response.json();
        setPullRequests((data.items ?? []).slice(0, MAX_HOME_ITEMS));
      } catch (error) {
        console.error('Error fetching pull requests:', error);
        setPullRequests([]);
        setError('Los pull requests no están disponibles.');
      } finally {
        setLoading(false);
      }
    },
    [backendUrl],
  );

  useEffect(() => {
    const refreshCurrentUser = () => {
      void fetchPullRequests(githubUsername);
    };
    refreshCurrentUser();
    const interval = setInterval(
      refreshCurrentUser,
      PULL_REQUESTS_REFRESH_INTERVAL,
    );
    window.addEventListener('focus', refreshCurrentUser);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refreshCurrentUser);
    };
  }, [fetchPullRequests, githubUsername]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const user = await refreshIdentity();
    if (user.githubUsername === githubUsername) {
      await fetchPullRequests(user.githubUsername);
    }
  }, [fetchPullRequests, githubUsername, refreshIdentity]);

  return { pullRequests, loading, error, refresh };
}
